// Wave 5 Phase 2 — placeholder paywall route.
// Wave 5 Phase 3b — real purchase + restore flow (flag-gated via
// REVENUECAT_ENABLED). While the flag is OFF the route renders identically
// to Phase 2/3a: Subscribe stays disabled, Restore is hidden, dismiss
// resolves any pending generator with quotaExceeded.
//
// Rendered when:
//   1. Real 402 from a gated generator — invokeGenerator catches, dispatches
//      l2f:paywall-required, __root listener navigates. Phase 3b: also
//      stashes a pendingPaywallCall so successful purchase re-invokes the
//      original generator transparently.
//   2. Dev override ?force_paywall=1 — invokeGenerator simulates 402 +
//      stashes pending entry.
//   3. Direct visit /paywall — no pending entry, dismiss just navigates.

import { useCallback, useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { toast } from "sonner";
import { Capacitor } from "@capacitor/core";
import { InAppBrowser, DefaultWebViewOptions } from "@capacitor/inappbrowser";
import { trackEvent } from "@/lib/analytics";
import { REVENUECAT_ENABLED } from "@/lib/featureFlags";
import { resolvePaywallCall } from "@/lib/invokeGenerator";
import {
  getOfferings,
  purchasePackage,
  restorePurchases,
  mapPurchaseErrorToCopy,
  RC_ENTITLEMENT_ID,
  type RcOffering,
  type RcPackage,
} from "@/lib/subscription";
import { supabase } from "@/integrations/supabase/client";

const paywallSearchSchema = z.object({
  generator: fallback(z.string(), "unknown").default("unknown"),
  source: fallback(z.string(), "direct").default("direct"),
  outfit_count: fallback(z.number().int().min(0), 0).default(0),
  quota: fallback(z.number().int().min(0), 5).default(5),
  reset_at: fallback(z.string(), "").default(""),
});

export const Route = createFileRoute("/paywall")({
  validateSearch: zodValidator(paywallSearchSchema),
  head: () => ({
    meta: [
      { title: "Ready for unlimited? — Like2Fit" },
      { name: "description", content: "Upgrade to keep analysing outfits without limits." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PaywallPlaceholder,
});

const CORAL = "#C84B2F";
const INK = "#2C2820";
const BODY = "#4A4540";
const HAIRLINE = "#E8E2D9";
const CANVAS = "#FBF7F0";

function purchasesReady(): boolean {
  return Capacitor.isNativePlatform() && REVENUECAT_ENABLED;
}


function PaywallPlaceholder() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mountedAtRef = useRef<number>(typeof performance !== "undefined" ? performance.now() : Date.now());
  const [plan, setPlan] = useState<"monthly" | "yearly">("yearly");
  const [purchasing, setPurchasing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  // Track whether dismissal path should resolve pending generator promise
  // as quotaExceeded (default) or was already resolved by purchase/restore.
  const pendingResolvedRef = useRef(false);

  // Offerings fetch — lazy, cached 5min, disabled when purchases not ready.
  const offeringsQuery = useQuery<RcOffering | null>({
    queryKey: ["revenuecat", "offerings"],
    enabled: purchasesReady(),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    queryFn: async () => {
      const off = await getOfferings();
      if (!off) {
        trackEvent("offerings_load_failed", { error_code: "null_offering" });
      }
      return off;
    },
  });

  // paywall_shown on mount / paywall_dismissed on unmount (with dwell_ms).
  useEffect(() => {
    trackEvent("paywall_shown", {
      generator: search.generator,
      source: search.source,
      outfit_count: search.outfit_count,
      quota: search.quota,
    });
    const startedAt = mountedAtRef.current;
    return () => {
      const now = typeof performance !== "undefined" ? performance.now() : Date.now();
      trackEvent("paywall_dismissed", {
        generator: search.generator,
        source: search.source,
        dwell_ms: Math.round(now - startedAt),
      });
      // On unmount, if no purchase/restore resolved the pending generator,
      // resolve it as dismissal (quotaExceeded shape).
      if (!pendingResolvedRef.current) {
        void resolvePaywallCall(false);
      }
    };
    // Fire once per mount; search values captured on first render intentionally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedPackage: RcPackage | null =
    plan === "monthly"
      ? offeringsQuery.data?.monthly ?? null
      : offeringsQuery.data?.annual ?? null;

  const subscribeEnabled =
    purchasesReady() && !!selectedPackage && !purchasing && !restoring;

  const primeOptimisticCache = useCallback(
    async (expirationDate: string | null, productId: string) => {
      try {
        const { data } = await supabase.auth.getUser();
        const userId = data.user?.id;
        if (!userId) return;
        queryClient.setQueryData(["subscription", userId], {
          subscription_tier: "premium",
          subscription_status: "active",
          subscription_expires_at: expirationDate,
        });
        // After ~3s, invalidate so the webhook-written row replaces cache.
        window.setTimeout(() => {
          void queryClient.invalidateQueries({ queryKey: ["subscription", userId] });
        }, 3000);
        try {
          window.dispatchEvent(
            new CustomEvent("l2f:subscription-activated", {
              detail: { product_id: productId, expires_at: expirationDate },
            }),
          );
        } catch {
          /* no-op */
        }
      } catch (err) {
        console.warn("[paywall] optimistic cache write failed", err);
      }
    },
    [queryClient],
  );

  const handleSubscribeTap = async () => {
    if (!subscribeEnabled || !selectedPackage) {
      // Retain legacy analytics event for placeholder-mode taps.
      trackEvent("paywall_subscribe_tapped", {
        generator: search.generator,
        source: search.source,
        plan_intent: plan,
      });
      return;
    }

    const productId = selectedPackage.product.identifier;
    const startedAt = performance.now();
    trackEvent("subscribe_attempted", {
      product_id: productId,
      package_type: plan,
      source: search.source,
      generator: search.generator,
    });
    setPurchasing(true);
    try {
      const outcome = await purchasePackage(selectedPackage);
      const latency = Math.round(performance.now() - startedAt);
      if (outcome.ok) {
        trackEvent("subscribe_succeeded", {
          product_id: outcome.productIdentifier,
          price: selectedPackage.product.price,
          currency: selectedPackage.product.currencyCode,
          latency_ms: latency,
        });
        await primeOptimisticCache(outcome.expirationDate, outcome.productIdentifier);
        pendingResolvedRef.current = true;
        void resolvePaywallCall(true);
        toast.success("You're in — welcome to Premium");
        navigate({ to: "/" });
        return;
      }
      if (outcome.cancelled) {
        trackEvent("subscribe_cancelled", {
          product_id: productId,
          latency_ms: latency,
        });
        return;
      }
      trackEvent("subscribe_failed", {
        product_id: productId,
        error_code: outcome.errorCode,
        error_message: outcome.message,
        latency_ms: latency,
      });
      toast.error(mapPurchaseErrorToCopy(outcome.errorCode));
    } finally {
      setPurchasing(false);
    }
  };

  const handleRestoreTap = async () => {
    if (!purchasesReady() || restoring || purchasing) return;
    trackEvent("restore_attempted", { source: search.source });
    setRestoring(true);
    try {
      const outcome = await restorePurchases();
      if (outcome.ok && outcome.hadActive) {
        trackEvent("restore_succeeded", {
          entitlement_id: RC_ENTITLEMENT_ID,
          expires_at: outcome.expirationDate,
        });
        await primeOptimisticCache(outcome.expirationDate, "restore");
        pendingResolvedRef.current = true;
        void resolvePaywallCall(true);
        toast.success("Subscription restored");
        navigate({ to: "/" });
        return;
      }
      if (outcome.ok) {
        trackEvent("restore_no_active_subscription", {});
        toast("No active subscription found");
        return;
      }
      toast.error("Couldn't restore. Try again.");
    } finally {
      setRestoring(false);
    }
  };

  const handleDismiss = () => {
    // Preserve ?force_paywall=1 for iterative dev testing per Phase 2 decision.
    const preserveForce =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("force_paywall") === "1";
    if (preserveForce) {
      navigate({ to: "/", search: { force_paywall: "1" } as never });
    } else {
      navigate({ to: "/" });
    }
    // resolvePaywallCall(false) fires in unmount cleanup.
  };

  const showRestore = purchasesReady();
  const subscribeLabel = purchasing
    ? "Processing…"
    : selectedPackage
    ? `Subscribe · ${selectedPackage.product.priceString}`
    : "Subscribe";

  // Compliance footer price labels — dynamic from RC when available, hardcoded fallback otherwise.
  const monthlyPriceLabel = offeringsQuery.data?.monthly?.product.priceString
    ? `${offeringsQuery.data.monthly.product.priceString}/month`
    : "£8.99/month";
  const yearlyPriceLabel = offeringsQuery.data?.annual?.product.priceString
    ? `${offeringsQuery.data.annual.product.priceString}/year`
    : "£59.99/year";

  const legalLinkStyle: React.CSSProperties = {
    background: "transparent",
    border: "none",
    padding: 0,
    color: INK,
    fontFamily: "'Work Sans', sans-serif",
    fontSize: 11,
    fontWeight: 500,
    textDecoration: "underline",
    textUnderlineOffset: 2,
    cursor: "pointer",
  };

  const openLegalLink = async (url: string) => {
    try {
      if (Capacitor.isNativePlatform()) {
        await InAppBrowser.openInWebView({ url, options: DefaultWebViewOptions });
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      console.warn("[paywall] failed to open legal link", err);
    }
  };


  return (
    <div
      style={{
        minHeight: "100vh",
        background: CANVAS,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          background: "#fff",
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 20,
          padding: "32px 24px",
          boxShadow: "0 20px 60px rgba(44, 40, 32, 0.08)",
        }}
      >
        <h1
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontSize: 30,
            lineHeight: 1.15,
            color: INK,
            margin: 0,
            textAlign: "center",
          }}
        >
          Ready for unlimited?
        </h1>
        <p
          style={{
            marginTop: 12,
            fontFamily: "'Work Sans', sans-serif",
            fontSize: 15,
            lineHeight: 1.5,
            color: BODY,
            textAlign: "center",
          }}
        >
          You&apos;ve used your {search.quota} free analyses. Upgrade to keep going.
        </p>

        {/* Plan toggle. */}
        <div
          style={{
            marginTop: 24,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            padding: 4,
            background: CANVAS,
            border: `1px solid ${HAIRLINE}`,
            borderRadius: 12,
          }}
        >
          {(["monthly", "yearly"] as const).map((p) => {
            const active = plan === p;
            return (
              <button
                key={p}
                type="button"
                onClick={() => setPlan(p)}
                style={{
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "none",
                  background: active ? "#fff" : "transparent",
                  color: active ? INK : BODY,
                  fontFamily: "'Work Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: active ? 600 : 500,
                  cursor: "pointer",
                  boxShadow: active ? "0 1px 3px rgba(44,40,32,0.08)" : "none",
                  textTransform: "capitalize",
                }}
              >
                {p}
                {p === "yearly" && (
                  <span
                    style={{
                      display: "inline-block",
                      marginLeft: 6,
                      fontSize: 11,
                      color: CORAL,
                      fontWeight: 600,
                    }}
                  >
                    Best value
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Subscribe CTA — enabled only when purchases ready + package loaded. */}
        <button
          type="button"
          onClick={handleSubscribeTap}
          disabled={!subscribeEnabled}
          style={{
            marginTop: 20,
            width: "100%",
            padding: "14px 16px",
            borderRadius: 12,
            border: "none",
            background: CORAL,
            color: "#fff",
            fontFamily: "'Work Sans', sans-serif",
            fontSize: 15,
            fontWeight: 600,
            cursor: subscribeEnabled ? "pointer" : "not-allowed",
            opacity: subscribeEnabled ? 1 : 0.55,
          }}
        >
          {subscribeLabel}
        </button>
        {!subscribeEnabled && (
          <p
            style={{
              marginTop: 8,
              fontFamily: "'Work Sans', sans-serif",
              fontSize: 12,
              color: BODY,
              textAlign: "center",
              opacity: 0.75,
            }}
          >
            {purchasesReady()
              ? offeringsQuery.isLoading
                ? "Loading plans…"
                : "Plans unavailable right now"
              : "Coming soon"}
          </p>
        )}

        {/* Apple Guideline 3.1.2(c) compliance footer — subscription details + EULA + privacy. */}
        <p
          style={{
            marginTop: 16,
            fontFamily: "'Work Sans', sans-serif",
            fontSize: 11,
            lineHeight: 1.5,
            color: BODY,
            textAlign: "center",
            opacity: 0.75,
          }}
        >
          Like2Fit Pro Monthly ({monthlyPriceLabel}) or Like2Fit Pro Yearly ({yearlyPriceLabel}).
          Subscriptions auto-renew unless cancelled 24 hours before the current period ends.
          Manage or cancel in your Apple ID subscription settings.
          <br />
          <button
            type="button"
            onClick={() => openLegalLink("https://www.apple.com/legal/internet-services/itunes/dev/stdeula/")}
            style={legalLinkStyle}
          >
            Terms of Use
          </button>
          {" · "}
          <button
            type="button"
            onClick={() => openLegalLink("https://www.like2fit.com/privacy")}
            style={legalLinkStyle}
          >
            Privacy Policy
          </button>
        </p>


        {showRestore && (
          <button
            type="button"
            onClick={handleRestoreTap}
            disabled={restoring || purchasing}
            style={{
              marginTop: 12,
              width: "100%",
              padding: "12px 16px",
              borderRadius: 10,
              border: `1px solid ${HAIRLINE}`,
              background: "#fff",
              color: INK,
              fontFamily: "'Work Sans', sans-serif",
              fontSize: 14,
              fontWeight: 500,
              cursor: restoring || purchasing ? "not-allowed" : "pointer",
              opacity: restoring || purchasing ? 0.6 : 1,
            }}
          >
            {restoring ? "Restoring…" : "Restore purchases"}
          </button>
        )}

        <button
          type="button"
          onClick={handleDismiss}
          style={{
            marginTop: 20,
            width: "100%",
            padding: "12px 16px",
            borderRadius: 10,
            border: "none",
            background: "transparent",
            color: INK,
            fontFamily: "'Work Sans', sans-serif",
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
            textDecoration: "underline",
            textUnderlineOffset: 3,
          }}
        >
          Not now
        </button>
      </div>
    </div>
  );
}
